import { useEffect } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft,
    ArrowUpRight,
    Quote,
    CheckCircle2,
    Download,
    FileText,
} from "lucide-react";

// ───────────────────────────────────────────────────────────────────
// CASE STUDY DATA
// ───────────────────────────────────────────────────────────────────

const CASE_STUDIES = {
    lyncfit: {
        slug: "lyncfit",
        sector: "Strategy · UK Higher Education",
        partner: "180 Degrees Consulting — Imperial × QMUL",
        year: "2025 — 2026",
        role: "Associate Consultant · Multi-stakeholder GTM lead",
        headline:
            "Translating LyncFit into a UK Higher Education engagement strategy",
        subdeck:
            "A society-first wedge into a £40B+ market — projecting 5.8k–17.4k retained students and £161M–£484M of protected tuition revenue.",
        metrics: [
            { value: "£161–484M", label: "tuition revenue protected" },
            { value: "5.8k – 17.4k", label: "UK students retained annually" },
            { value: "28%", label: "of UK undergraduates considering leaving" },
            { value: "£500k", label: "Q4-2026 seed-close anchored" },
        ],
        downloadUrl:
            "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/ik3j7919_LyncFit_Hanan%20Hakim.pdf",
        downloadLabel: "Strategy roadmap (PDF)",
        nextSlug: "desnz",
        nextTitle: "Pricing grid-scale storage for DESNZ",
        sections: [
            {
                eyebrow: "Context",
                title: "Why this matters now",
                body: [
                    "UK Higher Education is a £40B+ sector under unprecedented pressure. 28% of undergraduates considered leaving in 2023, 5.3% actually do every year, and each dropout costs an institution ~£28k in foregone tuition and replacement cost.",
                    "The drivers have shifted. Mental health (34%), wellbeing (43%) and belonging are the leading reasons students cite — not academic performance. Yet incumbent platforms focus on administration and communication, not engagement or belonging.",
                    "OfS Condition B3 now sets numerical thresholds for continuation, completion and progression. Retention has moved from a soft KPI to a regulator-grade financial risk.",
                ],
            },
            {
                eyebrow: "The challenge",
                title: "A wellbeing-tech startup with no credible HE wedge",
                body: [
                    "LyncFit's interest-based peer-matching and embedded engagement signals were a clean fit for belonging — the largest unmet driver of attrition. But the UK HE sales cycle is brutal: multi-stakeholder, regulator-shadowed, and dominated by incumbent admin platforms.",
                    "Leadership needed a credible market-entry wedge, a phased rollout it could finance against, and a way to navigate Student Union, Welfare and Senior Leadership all at once — without spreading itself thin.",
                ],
            },
            {
                eyebrow: "The approach",
                title: "Society-first wedge, four-phase rollout",
                body: [
                    "I owned the multi-stakeholder GTM workstream at 180 Degrees Consulting (Imperial × QMUL). The core thesis: start where engagement is most visible, cheapest to win, and generates the data needed to unlock the bigger sale.",
                ],
                phases: [
                    {
                        n: "Phase 1",
                        name: "Society Digitalisation",
                        when: "Year 1 · 5 societies × 2 universities",
                        body: "Win the engagement layer; build product-market fit; secure lighthouse universities. WTP £1–2 / student / year.",
                    },
                    {
                        n: "Phase 2",
                        name: "Wellbeing & Belonging",
                        when: "Year 2 · +8 universities",
                        body: "Layer wellbeing check-ins, MH signposting and at-risk flags on top of engagement data. WTP £2–3.",
                    },
                    {
                        n: "Phase 3",
                        name: "Retention Infrastructure",
                        when: "Year 3 · 30+ universities (institutional)",
                        body: "Position LyncFit as the engagement signal layer for B3 reporting and at-risk programmes. WTP £3–5.",
                    },
                    {
                        n: "Phase 4",
                        name: "Insights & Analytics",
                        when: "Year 3+ premium",
                        body: "Sector-level benchmarks and analyst-grade dashboards. +25–40% premium uplift on installed base.",
                    },
                ],
            },
            {
                eyebrow: "The result",
                title: "Quantified at the board level",
                body: [
                    "A Python-based ML/NLP retention-impact simulator projects 5.8k–17.4k UK students retained annually with a 1–3% engagement lift — protecting £161M–£484M of UK tuition revenue across three years.",
                    "The model translated directly into pricing tiers (£1–5 / student / year) and a Year-1 plan to close two paid institutional contracts and a £500k seed by Q4-2026.",
                ],
                bullets: [
                    "Three-tier university segmentation: Engaged Russell Group → Post-92s with B3 exposure → Top-brand Russell Group.",
                    "MUST/SHOULD/COULD feature prioritisation feeding a Q1-2026 MVP launch.",
                    "Four-lane partnership ecosystem: Pilot Adoption · Distribution · Wellbeing Credibility · Monetisation.",
                ],
            },
        ],
        pullQuote: {
            text: "The retention problem isn't academic — it's belonging. The cheapest place to detect it is at the society layer. Win there, and the rest of the sale gets a lot less scary.",
            attr: "Working note · multi-stakeholder GTM workstream",
        },
        methodology: [
            "Industry data: HESA, OfS, HEPI, Student Minds, Universities UK.",
            "Custom Python ML/NLP retention-impact simulator across cohort, programme and engagement variables.",
            "Buyer interviews and willingness-to-pay synthesis across SUs and university leadership.",
        ],
    },
    tara: {
        slug: "tara",
        sector: "Innovation · AI for Development",
        partner: "Imperial College London × UCL × University of Hamburg",
        year: "2025",
        role: "Co-lead · Cost model + multi-level intervention architecture",
        headline: "TARA — closing the migrant-student transition gap",
        subdeck:
            "An AI-powered, WhatsApp-native chatbot supporting Indonesian student migrants in the UK. Geneva Challenge 2025 Global Finalist.",
        metrics: [
            { value: "< US$0.50", label: "cost per interaction" },
            { value: "~£5", label: "cost per student reached" },
            { value: "US$96k", label: "national investment protected / scholar-year" },
            { value: "90.9%", label: "WhatsApp penetration leveraged" },
        ],
        downloadUrl:
            "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/kbyw10wk_TARA_Geneva_Challenge_2025_Strategy_Deck.pdf",
        downloadLabel: "TARA strategy deck (PDF)",
        nextSlug: "desnz",
        nextTitle: "Pricing grid-scale storage for DESNZ",
        sections: [
            {
                eyebrow: "Context",
                title: "A ~US$962M investment at risk every year",
                body: [
                    "Indonesia invests heavily in sending its brightest students abroad — ~US$962M annually flows to UK universities through scholarships like LPDP, on the explicit expectation that scholars return and contribute to national development.",
                    "But a preventable transition gap — language, NHS, housing, visas, food, wellbeing — quietly erodes that investment. Students disengage, drop out or stay disconnected, producing 'brain waste' instead of returnable human capital.",
                ],
            },
            {
                eyebrow: "The challenge",
                title: "A multi-level problem with no single owner",
                body: [
                    "The gap sits across three failing layers at once: institutional (universities don't know who needs what), community (scholar associations are stretched thin), and individual (students don't know what they don't know until it's too late).",
                    "Any solution had to be culturally attuned, bilingual, 24/7, low-friction, and cheap enough to scale to thousands of students without grant-funded gatekeeping.",
                ],
            },
            {
                eyebrow: "The approach",
                title: "WhatsApp-native, bilingual, multi-level",
                body: [
                    "We chose to meet students where they already are. 90.9% of Indonesians use WhatsApp daily. No app installs, no onboarding tax, no learning curve.",
                ],
                phases: [
                    {
                        n: "Phase 1",
                        name: "Pilot — 2–3 UK campuses",
                        when: "Co-creation with PPI UK, university international offices, LPDP",
                        body: "Step-by-step guidance across academics, NHS, housing, visas, food and wellbeing — in Bahasa Indonesia and English.",
                    },
                    {
                        n: "Phase 2",
                        name: "UK-wide rollout",
                        when: "Layered partnerships with host universities",
                        body: "Verified-info loop with continuous learning from every interaction; ethics & data-protection officers embedded.",
                    },
                    {
                        n: "Phase 3",
                        name: "Replication toolkit",
                        when: "Other nationalities · other host countries · labour migrants",
                        body: "TARA's architecture is transferable. The same multi-level model maps to Indonesian workers abroad and to migrants of other origins.",
                    },
                ],
            },
            {
                eyebrow: "The result",
                title: "Decision-grade economics",
                body: [
                    "Cost per interaction under US$0.50 vs. US$15–25 for equivalent human support. ~£5 per student reached protects ~US$96k of national investment per scholar-year.",
                ],
                bullets: [
                    "Selected as a Geneva Challenge 2025 Global Finalist (top from 100+ international teams).",
                    "Aligned with SDGs 3 · 4 · 10 · 17 (health & wellbeing, quality education, reduced inequalities, partnerships).",
                    "Built a 5-person team across Imperial, UCL and Hamburg with a co-creation governance stack.",
                ],
            },
        ],
        pullQuote: {
            text: "If you spend US$962M sending people somewhere, the rational thing to do is spend the next US$5 helping them land. TARA is that landing.",
            attr: "Geneva Challenge 2025 — submission rationale",
        },
        methodology: [
            "Demographic + economic data: HESA, ONS, DataReportal, LPDP open reports.",
            "Cost modelling against published human-support unit economics and AI inference benchmarks.",
            "Stakeholder co-creation across students, scholar associations, universities, AI developers and government bodies.",
        ],
    },
    desnz: {
        slug: "desnz",
        sector: "Energy Transition · UK Government",
        partner: "Imperial College London × UCL × Cambridge — for UK DESNZ",
        year: "2024 — 2025",
        role: "Research Energy Analyst · Techno-economic module architect",
        headline: "Pricing grid-scale storage for DESNZ",
        subdeck:
            "A three-university, DESNZ-commissioned techno-economic assessment of grid-scale storage — synthesising regulatory and TRL dynamics into a decision-grade £49–66/MWh pricing band.",
        metrics: [
            { value: "£49–66/MWh", label: "grid-scale storage pricing band" },
            { value: "3 univs", label: "Imperial · UCL · Cambridge" },
            { value: "5", label: "regulatory jurisdictions synthesised" },
            { value: "~10", label: "sensitivity inputs stress-tested" },
        ],
        downloadUrl:
            "https://www.theuniversitysustainabilityinitiative.org/_files/ugd/be068c_def52c36780a4357b19418708e8d6d03.pdf",
        downloadLabel: "Read the published report (PDF)",
        nextSlug: "thesis",
        nextTitle:
            "Composition engineering of Ni-Co hydroxide for glycerol oxidation",
        sections: [
            {
                eyebrow: "Context",
                title: "Net-zero needs storage — but at what price?",
                body: [
                    "The UK's path to net zero leans heavily on grid-scale storage to firm intermittent renewables and balance the system. But the policy debate at the UK Department for Energy Security & Net Zero (DESNZ) kept stalling on the same question: what does storage actually cost, across the technologies that matter, under the regulations we can realistically expect?",
                    "Existing public estimates spanned wildly different assumptions on TRL, capex, opex, lifetime and grid services. The leadership team needed one defensible, sensitivity-tested number — not a survey of guesses.",
                ],
            },
            {
                eyebrow: "The challenge",
                title: "A decision-grade number across competing technologies",
                body: [
                    "I was asked to architect the techno-economic module of the three-university DESNZ-commissioned climate report (Imperial, UCL, Cambridge), with two non-negotiables: it had to be defensible under UK government scrutiny, and it had to be transparent enough that any reasonable analyst could rebuild the same number.",
                    "The hard work wasn't picking the model — it was synthesising the regulatory and TRL dynamics across jurisdictions so the cost answer was actually about storage technology, not about which country we squinted at.",
                ],
            },
            {
                eyebrow: "The approach",
                title: "From regulatory synthesis to a single price band",
                body: [
                    "I built the work in three layers: regulatory + TRL synthesis, a transparent techno-economic model, and a sensitivity stack that DESNZ stakeholders could prod without breaking.",
                ],
                phases: [
                    {
                        n: "Phase 1",
                        name: "Regulatory & TRL synthesis",
                        when: "Five jurisdictions",
                        body: "Mapped storage support schemes, capacity markets and TRL assumptions across 5 jurisdictions into a decision-grade briefing for leadership.",
                    },
                    {
                        n: "Phase 2",
                        name: "Techno-economic model",
                        when: "Imperial · UCL · Cambridge working group",
                        body: "Architected the techno-economic module: capex/opex stacks, lifetime, round-trip efficiency, capacity factor and grid-service revenue layered into an LCOS-style framework.",
                    },
                    {
                        n: "Phase 3",
                        name: "Sensitivity stress-test",
                        when: "~10 sensitivity inputs",
                        body: "Ran ~10 sensitivity inputs (cost of capital, lifetime, capex curve, market design, etc.) to bound the band and identify which assumptions actually move the answer.",
                    },
                ],
            },
            {
                eyebrow: "The result",
                title: "A £49–66/MWh band that shaped the report",
                body: [
                    "Delivered a £49–66/MWh pricing band that became the headline number for grid-scale storage in the final DESNZ-commissioned report. The band — not a point estimate — was deliberate: it forces the policy conversation to be about which sensitivities matter, not which spreadsheet wins.",
                ],
                bullets: [
                    "Findings shaped the leadership team's storage-technology positioning in the final report to DESNZ.",
                    "Methodology designed to be transparently reproducible by UK government analysts.",
                    "Regulatory & TRL briefing reused as decision input for downstream Imperial Energy Society workstreams.",
                ],
            },
        ],
        pullQuote: {
            text: "A point estimate hides where the politics live. A band makes it impossible to look away from them.",
            attr: "Working note · DESNZ techno-economic module",
        },
        methodology: [
            "Levelised cost of storage (LCOS) framework — capex, opex, lifetime, round-trip efficiency, cost of capital, grid-service revenue.",
            "Regulatory + TRL synthesis across five jurisdictions, briefed into the leadership team.",
            "~10 sensitivity inputs stress-tested; report scrutiny-ready for UK government stakeholders.",
            "Published as part of the three-university climate report (Imperial · UCL · Cambridge) — see download link.",
        ],
    },
    thesis: {
        slug: "thesis",
        sector: "Electrochemical Catalysis · Imperial MSc",
        partner: "Henry Royce Institute · Imperial College London — Dr. Reshma Rao",
        year: "2025",
        role: "MSc Researcher · Synthesis, characterisation & electrochemical testing",
        headline:
            "Composition engineering of Ni-Co hydroxide for active and stable glycerol oxidation",
        subdeck:
            "An MSc thesis at the Henry Royce Institute exploring how Ni–Co bimetallic hydroxides — electrodeposited onto FTO glass — can valorise crude biodiesel glycerol while lowering the energy cost of green hydrogen.",
        metrics: [
            { value: "Ni₀.₀₇₅Co₀.₀₂₅(OH)₂", label: "Optimal catalyst composition" },
            { value: "1.01 kΩ", label: "Charge-transfer resistance — minimised" },
            { value: "≈10×", label: "Rct improvement vs. pure Ni(OH)₂" },
            { value: "48%", label: "Projected rise in fossil-fuel demand (2012 → 2040, EIA)" },
        ],
        downloadUrl: null,
        downloadLabel: null,
        nextSlug: "lyncfit",
        nextTitle: "Translating LyncFit into a UK HE engagement strategy",
        sections: [
            {
                eyebrow: "Context",
                title: "Glycerol — a 10%-by-weight byproduct waiting for a job",
                body: [
                    "The Energy Information Administration projects a ~48% rise in global fossil-fuel consumption between 2012 and 2040. Biodiesel offsets part of that demand — but it also produces crude glycerol as ~10% of its mass output. That glycerol has historically been waste, incinerated or sold at depressed prices.",
                    "Electrochemical glycerol oxidation flips the script: convert that waste into high-value chemicals (glyceraldehyde, glycolic acid, formate) while simultaneously lowering the energy needed to split water for green hydrogen. The bottleneck has been finding earth-abundant catalysts that are both active and stable.",
                ],
            },
            {
                eyebrow: "The challenge",
                title: "Earth-abundant catalysts, alkaline conditions, real-world durability",
                body: [
                    "Nickel and cobalt hydroxides are known to be active for glycerol electrooxidation in alkaline media — but their performance is exquisitely sensitive to composition, surface state and morphology. The brief: find the Ni : Co ratio that maximises activity and durability while staying within a scalable, low-cost electrodeposition route on FTO glass.",
                ],
            },
            {
                eyebrow: "Methodology",
                title: "From bath to band — electrodeposition, then six characterisation techniques",
                body: [
                    "I fabricated NiₓCo_y(OH)₂ films by chronopotentiostatic electrodeposition (1 mA cm⁻² for 400 s) onto polished FTO substrates from aqueous nickel/cobalt nitrate baths of varying composition. After in-situ activation via CV staircase, each electrode passed through a full electrochemical and surface-state characterisation stack: CV, LSV (with iR correction and Tafel slopes), EIS, chronoamperometry, XPS (survey + high-resolution Ni 2p / Co 2p / O 1s), SEM and EDX.",
                ],
                figures: [
                    {
                        src: "/thesis/p13_i1.jpg",
                        label: "3",
                        caption:
                            "Schematic of the three-electrode electrodeposition set-up used to fabricate hierarchical Ni(OH)₂/Co(OH)₂ heterostructured nanoarrays on FTO substrate (Pt counter, Hg/HgO reference, Ni–Co/FTO working electrode).",
                    },
                    {
                        src: "/thesis/p14_i1.jpg",
                        label: "4",
                        caption:
                            "Chronopotentiograms during electrodeposition across the five Ni : Co compositions. Pure Ni(OH)₂ required the highest overpotential (~2.4 V) for nucleation onset; cobalt-rich films deposited at lower overpotentials.",
                    },
                    {
                        src: "/thesis/p14_i2.jpg",
                        label: "5",
                        caption:
                            "Optical images of the as-deposited films — visual confirmation of compositional dependence on film coverage and uniformity.",
                    },
                ],
            },
            {
                eyebrow: "Results",
                title: "Surface state and structure — what the spectra revealed",
                body: [
                    "XPS confirmed that the bulk cobalt is present as Co(OH)₂ (no spinel signatures), and that increasing Ni content tunes the electronic environment around the active Ni³⁺ states — the species responsible for selective glycerol oxidation. SEM showed a sharp morphology shift from dense Ni(OH)₂ nanosheets to interpenetrating Co(OH)₂ nanoflakes as the Ni : Co ratio is decreased.",
                ],
                figures: [
                    {
                        src: "/thesis/p17_i1.jpg",
                        label: "6",
                        caption:
                            "Survey + O 1s XPS spectra across compositions — schematic representation of the different components of Ni-Co catalysts.",
                    },
                    {
                        src: "/thesis/p18_i1.jpg",
                        label: "7",
                        caption:
                            "High-resolution XPS spectra of Ni 2p and Co 2p across all Ni–Co bimetallic compositions. Spectral shifts confirm electronic modulation between the two metal centres.",
                    },
                    {
                        src: "/thesis/p19_i1.jpg",
                        label: "8",
                        caption:
                            "SEM images of electrodeposited (a) Ni(OH)₂, (b) Ni₀.₀₇₅Co₀.₀₂₅(OH)₂, (c) Ni₀.₀₅Co₀.₀₅(OH)₂, (d) Ni₀.₀₂₅Co₀.₀₇₅(OH)₂ films. Morphology transitions visibly from compact nanosheets to interlocking flakes.",
                    },
                ],
            },
            {
                eyebrow: "Results",
                title: "Activity, kinetics and durability",
                body: [
                    "Cyclic voltammetry and linear-sweep curves showed that Ni₀.₀₇₅Co₀.₀₂₅(OH)₂ achieved the highest current densities at the lowest overpotentials for glycerol oxidation — and a Tafel slope consistent with a more facile rate-determining step. Nyquist analysis quantified the picture: charge-transfer resistance (Rct) dropped from ~10.9 kΩ for pure Ni(OH)₂ to ~1.01 kΩ at the optimal composition.",
                    "Chronoamperometric durability tests held the optimised electrode at constant potential for extended periods with negligible activity loss, confirming both intrinsic catalytic improvement and structural stability.",
                ],
                figures: [
                    {
                        src: "/thesis/p21_i1.jpg",
                        label: "9",
                        caption:
                            "Cyclic voltammetry and linear-sweep voltammetry of all Ni–Co compositions in 0.1 M KOH + glycerol, showing the activity ordering across the composition series.",
                    },
                    {
                        src: "/thesis/p23_i1.jpg",
                        label: "10",
                        caption:
                            "Nyquist plots of the various Ni–Co hydroxide electrodepositions recorded at 10 mV AC amplitude in 0.1 M KOH + glycerol electrolyte.",
                    },
                    {
                        src: "/thesis/p24_i1.jpg",
                        label: "11",
                        caption:
                            "Fitted equivalent-circuit parameters (Rct, Rs, CPE, Zw) extracted from the Nyquist plots — quantifying the kinetic advantage of Ni₀.₀₇₅Co₀.₀₂₅(OH)₂.",
                    },
                    {
                        src: "/thesis/p25_i1.jpg",
                        label: "12",
                        caption:
                            "Chronoamperometric durability test of Ni₀.₀₇₅Co₀.₀₂₅(OH)₂ — confirming sustained activity and structural integrity over time.",
                    },
                ],
                bullets: [
                    "Optimal composition: Ni₀.₀₇₅Co₀.₀₂₅(OH)₂ — minimum Rct and best LSV / Tafel performance.",
                    "Ni acts as the active catalytic centre; Co operates as a promoter via electronic modulation, not via direct oxidation.",
                    "Open-source workflow documented for reuse as a standard Royce Institute lab protocol.",
                ],
            },
        ],
        pullQuote: {
            text: "Cobalt isn't the catalyst here — it's the lever. The activity lives on nickel; cobalt just changes what nickel is willing to do.",
            attr: "Thesis · Henry Royce Institute, Imperial College London (2025)",
        },
        methodology: [
            "Electrodeposition onto polished FTO at 1 mA cm⁻² for 400 s across five Ni : Co compositions.",
            "Electrochemical stack: CV staircase activation, iR-corrected LSV, EIS, Tafel-slope analysis, chronoamperometric stability.",
            "Surface and structural characterisation: XPS (survey + high-resolution Ni 2p, Co 2p, O 1s), SEM imaging, EDX elemental quantification.",
            "Supervised by Dr. Reshma Rao at the Henry Royce Institute, Department of Materials, Imperial College London.",
        ],
    },
};

// ───────────────────────────────────────────────────────────────────
// SHARED BITS
// ───────────────────────────────────────────────────────────────────

const Overline = ({ children, className = "" }) => (
    <span
        className={`font-mono text-xs uppercase tracking-[0.22em] text-sienna ${className}`}
    >
        {children}
    </span>
);

const FadeUp = ({ children, delay = 0, className = "" }) => (
    <motion.div
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay, ease: [0.2, 0.7, 0.2, 1] }}
        className={className}
    >
        {children}
    </motion.div>
);

// ───────────────────────────────────────────────────────────────────
// PAGE
// ───────────────────────────────────────────────────────────────────

export default function CaseStudy() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const data = CASE_STUDIES[slug];

    useEffect(() => {
        if (data) {
            document.title = `${data.headline} — Hanan Hakim`;
            window.scrollTo({ top: 0, behavior: "instant" });
        }
    }, [data]);

    if (!data) return <Navigate to="/" replace />;

    return (
        <main
            data-testid={`case-study-page-${data.slug}`}
            className="bg-bone text-ink"
        >
            {/* Top nav */}
            <header className="border-b border-line">
                <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-20 h-16 md:h-20 flex items-center justify-between gap-6">
                    <Link
                        to="/"
                        data-testid="case-back-link"
                        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-ink transition-colors"
                    >
                        <ArrowLeft size={13} /> All work
                    </Link>
                    <Link
                        to="/"
                        className="font-serif text-xl tracking-tight"
                    >
                        Hanan{" "}
                        <span className="italic text-sienna">Hakim</span>
                    </Link>
                    <Link
                        to="/#recruiters"
                        className="hidden sm:inline-flex items-center gap-2 border border-ink px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink hover:bg-ink hover:text-bone transition-colors"
                    >
                        Get in touch <ArrowUpRight size={12} />
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="border-b border-line py-16 md:py-24">
                <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-20">
                    <FadeUp>
                        <Overline data-testid="case-sector">
                            Case study · {data.sector}
                        </Overline>
                    </FadeUp>
                    <FadeUp delay={0.08}>
                        <h1
                            data-testid="case-headline"
                            className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-ink mt-6 max-w-5xl"
                        >
                            {data.headline}
                        </h1>
                    </FadeUp>
                    <FadeUp delay={0.16}>
                        <p className="font-sans text-lg md:text-xl text-ink-soft leading-relaxed mt-8 max-w-3xl">
                            {data.subdeck}
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.24}>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-6 gap-x-10 mt-12 pt-8 border-t border-line max-w-3xl">
                            <div>
                                <Overline className="text-ink-soft">
                                    Partner
                                </Overline>
                                <p className="font-serif text-base text-ink mt-1 leading-snug">
                                    {data.partner}
                                </p>
                            </div>
                            <div>
                                <Overline className="text-ink-soft">Role</Overline>
                                <p className="font-serif text-base text-ink mt-1 leading-snug">
                                    {data.role}
                                </p>
                            </div>
                            <div>
                                <Overline className="text-ink-soft">Year</Overline>
                                <p className="font-serif text-base text-ink mt-1 leading-snug">
                                    {data.year}
                                </p>
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* Metric strip */}
            <section className="border-b border-line bg-bone-soft">
                <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-2 lg:grid-cols-4 divide-x divide-line">
                    {data.metrics.map((m, i) => (
                        <FadeUp key={i} delay={i * 0.05} className="py-10 px-4 md:px-6 min-w-0">
                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-sienna">
                                0{i + 1}
                            </p>
                            <p
                                data-testid={`case-metric-${i}`}
                                className={`font-mono text-ink mt-4 tracking-tight leading-[1.1] break-words ${
                                    String(m.value).length > 14
                                        ? "text-base sm:text-lg md:text-xl lg:text-2xl"
                                        : String(m.value).length > 8
                                          ? "text-xl sm:text-2xl md:text-3xl"
                                          : "text-2xl sm:text-3xl md:text-4xl"
                                }`}
                            >
                                {m.value}
                            </p>
                            <p className="font-sans text-sm text-ink-soft mt-3 leading-relaxed">
                                {m.label}
                            </p>
                        </FadeUp>
                    ))}
                </div>
            </section>

            {/* Sections */}
            {data.sections.map((s, i) => (
                <section
                    key={i}
                    data-testid={`case-section-${i}`}
                    className="border-b border-line py-16 md:py-24"
                >
                    <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-12 gap-6 md:gap-12">
                        <div className="col-span-12 md:col-span-4">
                            <FadeUp>
                                <Overline>
                                    {s.eyebrow}
                                </Overline>
                                <h2 className="font-serif text-3xl md:text-4xl text-ink mt-4 leading-[1.1]">
                                    {s.title}
                                </h2>
                            </FadeUp>
                        </div>
                        <div className="col-span-12 md:col-span-8 md:col-start-6 lg:col-start-6 lg:col-span-7">
                            <FadeUp delay={0.1}>
                                <div className="space-y-5 font-sans text-base md:text-lg leading-[1.7] text-ink-soft">
                                    {s.body.map((p, j) => (
                                        <p key={j}>{p}</p>
                                    ))}
                                </div>
                            </FadeUp>

                            {s.bullets && (
                                <FadeUp delay={0.18}>
                                    <ul className="mt-8 space-y-3 border-t border-line pt-6">
                                        {s.bullets.map((b, j) => (
                                            <li
                                                key={j}
                                                className="flex items-start gap-3 font-sans text-base text-ink leading-relaxed"
                                            >
                                                <CheckCircle2
                                                    size={16}
                                                    className="text-sienna mt-1.5 shrink-0"
                                                />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </FadeUp>
                            )}

                            {s.phases && (
                                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {s.phases.map((ph, j) => (
                                        <FadeUp
                                            key={j}
                                            delay={0.1 + j * 0.05}
                                            className="bg-bone-soft border border-line rounded-2xl p-5 hover:border-ink transition-colors"
                                        >
                                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-sienna">
                                                {ph.n}
                                            </p>
                                            <p className="font-serif text-xl text-ink mt-2 leading-snug">
                                                {ph.name}
                                            </p>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-soft mt-2">
                                                {ph.when}
                                            </p>
                                            <p className="font-sans text-sm text-ink-soft mt-3 leading-relaxed">
                                                {ph.body}
                                            </p>
                                        </FadeUp>
                                    ))}
                                </div>
                            )}

                            {s.figures && (
                                <div className="mt-10 space-y-10">
                                    {s.figures.map((fig, j) => (
                                        <FadeUp
                                            key={j}
                                            delay={0.1 + j * 0.04}
                                            className="border-t border-line pt-6"
                                            data-testid={`case-figure-${i}-${j}`}
                                        >
                                            <figure>
                                                <div className="bg-bone-soft border border-line p-3 md:p-5 rounded-xl overflow-hidden">
                                                    <img
                                                        src={fig.src}
                                                        alt={fig.alt || fig.caption}
                                                        loading="lazy"
                                                        className="w-full h-auto object-contain max-h-[640px] mx-auto"
                                                    />
                                                </div>
                                                <figcaption className="mt-4 flex items-start gap-3">
                                                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-sienna pt-1 shrink-0">
                                                        Fig. {fig.label || j + 1}
                                                    </span>
                                                    <span className="font-sans text-sm text-ink-soft leading-relaxed">
                                                        {fig.caption}
                                                    </span>
                                                </figcaption>
                                            </figure>
                                        </FadeUp>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            ))}

            {/* Pull quote */}
            <section className="border-b border-line py-20 md:py-28 bg-ink text-bone">
                <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-20">
                    <FadeUp>
                        <Quote size={32} className="text-sienna" />
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <blockquote
                            data-testid="case-pull-quote"
                            className="font-serif text-2xl md:text-4xl lg:text-[2.6rem] italic leading-[1.25] mt-6 max-w-5xl"
                        >
                            “{data.pullQuote.text}”
                        </blockquote>
                    </FadeUp>
                    <FadeUp delay={0.18}>
                        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-bone/50 mt-8">
                            — {data.pullQuote.attr}
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* Methodology + download */}
            <section className="border-b border-line py-16 md:py-20">
                <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-12 gap-6 md:gap-12">
                    <div className="col-span-12 md:col-span-4">
                        <Overline>Methodology</Overline>
                        <h2 className="font-serif text-3xl text-ink mt-4 leading-tight">
                            How the work <span className="italic">held up.</span>
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-8 md:col-start-6 lg:col-start-6 lg:col-span-7">
                        <ul className="space-y-4">
                            {data.methodology.map((m, j) => (
                                <li
                                    key={j}
                                    className="flex items-start gap-3 font-sans text-base text-ink-soft leading-relaxed border-b border-line pb-4"
                                >
                                    <FileText
                                        size={14}
                                        className="text-sienna mt-1.5 shrink-0"
                                    />
                                    <span>{m}</span>
                                </li>
                            ))}
                        </ul>
                        {data.downloadUrl && (
                            <a
                                href={data.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="case-download-deck"
                                className="mt-8 inline-flex items-center gap-2 border border-ink px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ink hover:bg-ink hover:text-bone transition-colors"
                            >
                                <Download size={13} /> {data.downloadLabel}
                            </a>
                        )}
                    </div>
                </div>
            </section>

            {/* Next case */}
            <section className="py-20 md:py-28">
                <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-20">
                    <Overline>Next case</Overline>
                    <button
                        onClick={() => navigate(`/case/${data.nextSlug}`)}
                        data-testid="case-next-link"
                        className="group block text-left mt-4"
                    >
                        <h3 className="font-serif text-3xl md:text-5xl text-ink leading-[1.05] group-hover:text-sienna transition-colors max-w-4xl">
                            {data.nextTitle}{" "}
                            <ArrowUpRight
                                className="inline-block -translate-y-1 group-hover:translate-x-1 group-hover:-translate-y-2 transition-transform"
                                size={32}
                            />
                        </h3>
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-ink text-bone/60 py-10 border-t border-line-strong">
                <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <p className="font-serif text-xl text-bone">
                        Hanan <span className="italic text-sienna">Hakim</span>
                    </p>
                    <Link
                        to="/"
                        className="font-mono text-[11px] uppercase tracking-[0.22em] hover:text-bone transition-colors"
                    >
                        ← Back to all work
                    </Link>
                </div>
            </footer>
        </main>
    );
}
